import { useMutation } from "@tanstack/react-query";
import { useFormik } from "formik";
import { useState } from "react";
import { Col, Row } from "react-bootstrap";
import {
  FiCheckCircle,
  FiEye,
  FiEyeOff,
  FiLock,
  FiMail,
  FiShield,
} from "react-icons/fi";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import * as yup from "yup";
import Loader from "../../components/Loader/Loader";
import useDetails from "../../hooks/useDetails";
import useSlider from "../../hooks/useSlider";
import { user } from "../../redux/features/userSlice";
import {
  updateSuperAdminEmail,
  updateSuperAdminPassword,
} from "../../services/services";
import { toastAlert } from "../../utils/SweetAlert";
import Sidebar from "../sidebar/Sidebar";
import "./accountSettings.css";

const AccountSettings = () => {
  const isSlider = useSlider();
  const details = useDetails();
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const [visiblePasswords, setVisiblePasswords] = useState({});

  const togglePassword = (field) => {
    setVisiblePasswords((current) => ({
      ...current,
      [field]: !current[field],
    }));
  };

  const emailMutation = useMutation({
    mutationFn: updateSuperAdminEmail,
    onSuccess: (response) => {
      const email = response?.data?.data?.email;
      dispatch(user({ email }));
      emailForm.resetForm({ values: { email, current_password: "" } });
      toastAlert("success", response?.data?.message);
    },
  });

  const passwordMutation = useMutation({
    mutationFn: updateSuperAdminPassword,
    onSuccess: (response) => {
      passwordForm.resetForm();
      toastAlert("success", response?.data?.message);
    },
  });

  const emailForm = useFormik({
    enableReinitialize: true,
    initialValues: {
      email: details?.email || "",
      current_password: "",
    },
    validationSchema: yup.object({
      email: yup
        .string()
        .trim()
        .email(t("accountSettings.validEmail"))
        .required(t("accountSettings.emailRequired")),
      current_password: yup
        .string()
        .required(t("accountSettings.currentPasswordRequired")),
    }),
    onSubmit: (values) => emailMutation.mutate(values),
  });

  const passwordForm = useFormik({
    initialValues: {
      current_password: "",
      new_password: "",
      confirm_password: "",
    },
    validationSchema: yup.object({
      current_password: yup
        .string()
        .required(t("accountSettings.currentPasswordRequired")),
      new_password: yup
        .string()
        .required(t("accountSettings.newPasswordRequired")),
      confirm_password: yup
        .string()
        .oneOf([yup.ref("new_password")], t("accountSettings.passwordsMustMatch"))
        .required(t("accountSettings.confirmPasswordRequired")),
    }),
    onSubmit: (values) => passwordMutation.mutate(values),
  });

  const fieldError = (form, field) =>
    form.touched[field] && form.errors[field] ? (
      <small className="text-danger ms-1">{form.errors[field]}</small>
    ) : null;

  const passwordField = (form, field, label, autoComplete) => {
    const inputId = `${form === emailForm ? "email" : "password"}-${field}`;
    const visibilityKey = `${inputId}-visibility`;
    const hasError = form.touched[field] && form.errors[field];

    return (
      <div className="account-field">
        <label htmlFor={inputId}>{label}</label>
        <div className={`account-input-wrap ${hasError ? "has-error" : ""}`}>
          <FiLock aria-hidden="true" />
          <input
            id={inputId}
            name={field}
            type={visiblePasswords[visibilityKey] ? "text" : "password"}
            value={form.values[field]}
            onChange={form.handleChange}
            onBlur={form.handleBlur}
            autoComplete={autoComplete}
            placeholder={label}
            aria-invalid={Boolean(hasError)}
          />
          <button
            type="button"
            className="password-toggle"
            onClick={() => togglePassword(visibilityKey)}
            aria-label={visiblePasswords[visibilityKey] ? t("accountSettings.hidePassword") : t("accountSettings.showPassword")}
          >
            {visiblePasswords[visibilityKey] ? <FiEyeOff /> : <FiEye />}
          </button>
        </div>
        {fieldError(form, field)}
      </div>
    );
  };

  return (
    <div className="mainbox">
      <Sidebar />
      <main className={isSlider ? "body-content close" : "body-content open"}>
        <div className="account-settings-page">
          <div className="account-page-heading">
            <div>
              <span className="account-eyebrow">{t("accountSettings.security")}</span>
              <h2>{t("accountSettings.title")}</h2>
              <p>{t("accountSettings.subtitle")}</p>
            </div>
            <div className="account-security-badge">
              <FiShield />
              <span>{t("accountSettings.protected")}</span>
            </div>
          </div>

          <section className="account-profile-strip">
            <div className="account-avatar">
              {details?.userInfo?.profilePhoto ? (
                <img src={details.userInfo.profilePhoto} alt="" />
              ) : (
                <span>{details?.userInfo?.name?.charAt(0)?.toUpperCase() || "A"}</span>
              )}
            </div>
            <div className="account-profile-copy">
              <h3>{details?.userInfo?.name || t("accountSettings.administrator")}</h3>
              <p>{details?.email || details?.userInfo?.email}</p>
            </div>
            <div className="account-active-status">
              <FiCheckCircle /> {t("accountSettings.activeAccount")}
            </div>
          </section>

          <Row className="g-4 account-settings-grid">
          <Col lg={6}>
            <section className="account-settings-card h-100">
              <div className="account-card-heading">
                <span className="account-card-icon email"><FiMail /></span>
                <div>
                  <h3>{t("accountSettings.changeEmail")}</h3>
                  <p>{t("accountSettings.emailDescription")}</p>
                </div>
              </div>
              <form onSubmit={emailForm.handleSubmit} noValidate>
                <div className="account-field">
                  <label htmlFor="account-email">{t("accountSettings.email")}</label>
                  <div className={`account-input-wrap ${emailForm.touched.email && emailForm.errors.email ? "has-error" : ""}`}>
                    <FiMail aria-hidden="true" />
                    <input id="account-email" name="email" type="email" value={emailForm.values.email} onChange={emailForm.handleChange} onBlur={emailForm.handleBlur} autoComplete="email" placeholder={t("accountSettings.email")} />
                  </div>
                  {fieldError(emailForm, "email")}
                </div>
                {passwordField(emailForm, "current_password", t("accountSettings.currentPassword"), "current-password")}
                <button
                  type="submit"
                  className="account-submit-btn"
                  disabled={emailMutation.isPending}
                >
                  <FiMail />
                  {t("accountSettings.updateEmail")}
                </button>
              </form>
            </section>
          </Col>

          <Col lg={6}>
            <section className="account-settings-card h-100">
              <div className="account-card-heading">
                <span className="account-card-icon password"><FiLock /></span>
                <div>
                  <h3>{t("accountSettings.changePassword")}</h3>
                  <p>{t("accountSettings.passwordDescription")}</p>
                </div>
              </div>
              <form onSubmit={passwordForm.handleSubmit} noValidate>
                {passwordField(passwordForm, "current_password", t("accountSettings.currentPassword"), "current-password")}
                {passwordField(passwordForm, "new_password", t("accountSettings.newPassword"), "new-password")}
                {passwordField(passwordForm, "confirm_password", t("accountSettings.confirmPassword"), "new-password")}
                <button
                  type="submit"
                  className="account-submit-btn"
                  disabled={passwordMutation.isPending}
                >
                  <FiLock />
                  {t("accountSettings.updatePassword")}
                </button>
              </form>
            </section>
          </Col>
          </Row>
        </div>
      </main>
      {(emailMutation.isPending || passwordMutation.isPending) && <Loader />}
    </div>
  );
};

export default AccountSettings;
